import React, { useEffect, useRef } from "react";

/**
 * Mobile-first horizontally scrollable tab strip.
 * - Smooth scroll + scroll-snap for tactile mobile UX
 * - Auto-scrolls the active tab into view when `active` changes (e.g. set programmatically)
 * - Hidden scrollbar
 * - Subtle right-edge gradient fade hints there's more content offscreen
 *
 * Props:
 *  - tabs:    [{ key, label }]
 *  - active:  current key
 *  - onChange: (key) => void
 */
export const ScrollableTabs = ({ tabs, active, onChange, className = "" }) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  // Auto-scroll active tab into view (helpful when switching programmatically or on first paint)
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      activeRef.current.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [active]);

  return (
    <div className={`relative mt-2 ${className}`}>
      <div
        ref={containerRef}
        className="flex gap-2 border-b pb-2 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
        role="tablist"
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            ref={t.key === active ? activeRef : null}
            role="tab"
            aria-selected={t.key === active}
            data-testid={`tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className={`snap-start px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
              t.key === active
                ? "bg-primary text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* Right-edge fade hint — purely cosmetic */}
      <div className="pointer-events-none absolute top-0 right-0 h-full w-6 bg-gradient-to-l from-white to-transparent" />
    </div>
  );
};

export default ScrollableTabs;
